import type { Account } from '../../types/account.js';
import { normalizeContentCreationConfig } from '../accountServices.js';

// Publishing — caption WILDCARDS (2026-07-28). Merge tokens usable inside a post
// caption, resolved PER ACCOUNT at publish/schedule time. This is the foundation
// for multi-account posting: a shared caption is only correct across accounts
// because the account-specific bits resolve per account.
//
// Author-then-resolve-per-account (the broadcast slug-engine model): the STORED
// post keeps the AUTHORED caption (tokens intact); resolution happens at DISPATCH,
// against the target account — so an edit-after-schedule re-resolves correctly,
// and a multi-account fan-out calls the SAME resolver once per target account.
//
// Unknown `{token}` is left LITERAL (typos stay discoverable — the templating.ts
// posture); an empty source resolves to '' (the token disappears).
//
// The registry is the ONE pure source (token → label + resolve-from-context), so
// adding a token later is one line here (+ a WildcardContext field + its builder).

// The per-account inputs the wildcards resolve from — flattened + null-coalesced
// so the SAME pure resolver runs SERVER-side (postService, before dispatch) and
// CLIENT-side (the composer's live preview) without shipping the account's
// internals (serviceConfig) to the browser. The server builds it from the account
// via wildcardContextFromAccount; the platforms endpoint ships the built context.
export interface WildcardContext {
  dealershipName: string; // account.name
  salesContactDetails: string; // serviceConfig.contentCreation.salesContactDetailsForPost
  serviceContactDetails: string; // serviceConfig.contentCreation.serviceContactDetailsForPost
  marque: string; // the brand account's name (via computeBrandAncestorMap)
  website: string; // account.websiteUrl
  phone: string; // account.phone
}

export const EMPTY_WILDCARD_CONTEXT: WildcardContext = {
  dealershipName: '',
  salesContactDetails: '',
  serviceContactDetails: '',
  marque: '',
  website: '',
  phone: '',
};

export interface WildcardDef {
  /** The bare token (no braces), e.g. 'dealership_name'. */
  token: string;
  /** Menu label. */
  label: string;
  /** One-line help for the insert menu. */
  description: string;
  /** Pull this token's value out of the resolved context. */
  resolve: (ctx: WildcardContext) => string;
}

// LOCKED set — exactly these six (Scott; five at 2026-07-28, the contact-details
// pair since the 2026-08-05 split). Do NOT add suburb / city / state / group.
// Order here is the menu order.
export const CAPTION_WILDCARDS: WildcardDef[] = [
  {
    token: 'dealership_name',
    label: 'Account name',
    description: "The account's name",
    resolve: (c) => c.dealershipName,
  },
  {
    token: 'marque',
    label: 'Marque',
    description: 'The brand / marque name',
    resolve: (c) => c.marque,
  },
  {
    token: 'website',
    label: 'Website',
    description: "The account's website URL",
    resolve: (c) => c.website,
  },
  {
    token: 'phone',
    label: 'Phone',
    description: "The account's business phone",
    resolve: (c) => c.phone,
  },
  {
    token: 'sales_contact_details',
    label: 'Sales contact details',
    description: 'The Content Creation "sales contact details for post" block',
    resolve: (c) => c.salesContactDetails,
  },
  {
    token: 'service_contact_details',
    label: 'Service contact details',
    description: 'The Content Creation "service contact details for post" block',
    resolve: (c) => c.serviceContactDetails,
  },
];

// ⚠️⚠️ LEGACY ALIASES — RESOLVED but deliberately NOT offered in the insert menu.
//
// `{contact_details}` was the pre-2026-08-05 name for what is now
// `{sales_contact_details}`. Drafts and — the reason this is load-bearing —
// SCHEDULED posts already contain it, and an unknown token is left LITERAL by
// design, so dropping the alias would publish "{contact_details}" onto a client's
// page at some point in the future, with nobody watching. Keep this indefinitely;
// the cost is one map entry, the failure is a live incident.
export const LEGACY_WILDCARD_ALIASES: Record<string, (c: WildcardContext) => string> = {
  contact_details: (c) => c.salesContactDetails,
};

const RESOLVE_BY_TOKEN = new Map<string, (c: WildcardContext) => string>([
  ...CAPTION_WILDCARDS.map((w) => [w.token, w.resolve] as const),
  ...Object.entries(LEGACY_WILDCARD_ALIASES),
]);

// Match a `{lower_snake}` token. An unknown token (or any other brace content —
// uppercase, digits, etc.) simply won't resolve and is left literal.
const TOKEN_PATTERN = /\{([a-z_]+)\}/g;

/**
 * Replace every KNOWN `{token}` in a caption with its resolved value; leave an
 * unknown token literal; an empty source resolves to ''.
 */
export function resolveCaptionWildcards(caption: string, ctx: WildcardContext): string {
  return caption.replace(TOKEN_PATTERN, (match, token: string) => {
    const resolve = RESOLVE_BY_TOKEN.get(token);
    return resolve ? resolve(ctx) : match;
  });
}

// The account fields the context is built from. A Pick so the builder is
// unit-testable off a plain account shape.
export type WildcardAccount = Pick<Account, 'name' | 'websiteUrl' | 'phone' | 'serviceConfig'>;

/**
 * Build the resolution context from the target account + its resolved marque
 * (the brand account's name, resolved via brandAccountId → computeBrandAncestorMap
 * so it matches the marque everywhere else; '' when the account has no brand).
 */
export function wildcardContextFromAccount(
  account: WildcardAccount,
  marque: string,
): WildcardContext {
  // Route the contact block through the shared normaliser rather than reading the
  // fields directly: this feeds LIVE captions, so a doc that still carries only
  // the pre-2026-08-05 `contactDetailsForPost` must resolve to its value, not ''.
  const cc = account.serviceConfig?.contentCreation;
  const contact = cc ? normalizeContentCreationConfig(cc) : null;
  return {
    dealershipName: account.name,
    salesContactDetails: contact?.salesContactDetailsForPost ?? '',
    serviceContactDetails: contact?.serviceContactDetailsForPost ?? '',
    marque: marque ?? '',
    website: account.websiteUrl ?? '',
    phone: account.phone ?? '',
  };
}

/**
 * The most accounts one batch wildcard-context request may name (Post to Many's
 * per-account preview). ⚠️ Past this the request is REFUSED, never truncated: a
 * silently truncated response would preview a subset while looking complete,
 * which is exactly the failure that preview exists to catch.
 */
export const MAX_WILDCARD_CONTEXT_ACCOUNTS = 300;

/**
 * ⚠️⚠️ THE ONE BUILDER of WildcardContexts from the roster (2026-10-01). Every
 * server path that needs a context — dispatch (resolvePostContext), the single
 * account platforms endpoint, the caption drafter and the Post-to-Many batch
 * preview — goes through this, over ONE roster read and ONE brand-ancestor map.
 * Two builders would drift, and what drifts is what the preview promises versus
 * what actually publishes.
 *
 * Returns one entry per requested id; an id that is not in the roster maps to
 * null (the caller shows it as failed — never a silently skipped row).
 * `marque` is the brand-type ancestor's name via the canonical
 * computeBrandAncestorMap (passed in, since it lives in the accounts module), ''
 * when there is none — the same answer resolveAccountMarqueName gives.
 */
export function wildcardContextsFromRoster(
  roster: ReadonlyArray<WildcardAccount & { id: string }>,
  brandAncestorId: ReadonlyMap<string, string | null>,
  ids: readonly string[],
): Record<string, WildcardContext | null> {
  const byId = new Map(roster.map((a) => [a.id, a]));
  const out: Record<string, WildcardContext | null> = {};
  for (const id of ids) {
    const account = byId.get(id);
    if (!account) {
      out[id] = null;
      continue;
    }
    const brandId = brandAncestorId.get(id) ?? null;
    const marque = brandId ? (byId.get(brandId)?.name ?? '') : '';
    out[id] = wildcardContextFromAccount(account, marque);
  }
  return out;
}

/**
 * Resolve the wildcards in a post's base caption AND every per-destination caption
 * override, against ONE account's context. Generic so it preserves the caller's
 * richer target shape (the server's MappablePost). Used at dispatch; the stored
 * post is untouched (it keeps the authored tokens).
 */
export function resolvePostCaptions<
  T extends { content: string; targets: Array<{ customContent?: string | null }> },
>(post: T, ctx: WildcardContext): T {
  return {
    ...post,
    content: resolveCaptionWildcards(post.content, ctx),
    targets: post.targets.map((t) =>
      t.customContent != null
        ? { ...t, customContent: resolveCaptionWildcards(t.customContent, ctx) }
        : t,
    ),
  };
}
